<script setup lang="ts">
/**
 * `performance-schedule` zone: what's on, over a rolling range of days.
 *
 * The range rolls forward from the current programme day and is never a calendar
 * week (D12). Day headings read "Today" / "Tomorrow" and then weekday plus date,
 * in the screen's locale — chrome language is the screen's, independent of the
 * language the programme was imported in (D13).
 *
 * A cancelled performance stays in the list and is marked. Dropping it would
 * leave the audience standing in the foyer wondering.
 */
import { computed } from 'vue';
import type { PerformanceScheduleZoneParams, ProgrammePayload } from './programme-types';
import {
  DEFAULT_PROGRAMME_STRINGS,
  fillTemplate,
  formatDayLabel,
  formatLanguages,
  formatTime,
  groupByProgrammeDay,
  isInProgress,
  scopedPerformances,
  selectPerformanceList,
  surtitleBadges,
  type ProgrammeDayGroup,
  type ProgrammeStrings,
} from './programme';

const props = defineProps<{
  params: PerformanceScheduleZoneParams;
  payload: ProgrammePayload;
  /** Injected so selection is deterministic and testable at any instant. */
  now: Date;
  strings?: ProgrammeStrings | undefined;
}>();

const strings = computed<ProgrammeStrings>(() => props.strings ?? DEFAULT_PROGRAMME_STRINGS);

const selected = computed(() =>
  selectPerformanceList(scopedPerformances(props.payload, props.params), props.payload, props.now, {
    days: props.params.days,
    maxItems: props.params.maxItems,
  }),
);

/**
 * Grouping always produces groups; `groupBy: 'none'` renders one group with its
 * heading suppressed, so both variants share one template.
 */
const groups = computed<ProgrammeDayGroup[]>(() => {
  const { items, todayKey } = selected.value;
  if (props.params.groupBy === 'none') {
    return items.length === 0 ? [] : [{ dayKey: '', isToday: false, items: [...items] }];
  }
  return groupByProgrammeDay(items, todayKey);
});

/**
 * A zone covering more than one stage must name the stage on every row, whatever
 * the flag says: an `all`-scoped foyer board that lists times without locations
 * is unreadable. The flag exists to add location names to a single-stage board.
 */
const showLocation = computed(() => props.params.showLocation || props.params.scope !== 'screen');

const moreLabel = computed<string | null>(() =>
  selected.value.truncated > 0
    ? fillTemplate(strings.value.more, { count: selected.value.truncated })
    : null,
);

function dayLabel(dayKey: string): string {
  return formatDayLabel(dayKey, props.payload, strings.value, selected.value.todayKey);
}

function statusLabel(status: string): string | null {
  if (status === 'cancelled') return strings.value.cancelled;
  if (status === 'postponed') return strings.value.postponed;
  return null;
}

/**
 * One badge per surtitled language rather than a "Surtitles: English, German"
 * line: on a list of eight rows the badges are what a visitor scans for, and the
 * sentence would eat the row (D16). The sentence stays as the row's tooltip and
 * accessible name.
 */
function surtitles(languages: readonly string[]): Array<{ code: string; label: string }> {
  if (!props.params.showSurtitles) return [];
  return surtitleBadges(languages, props.payload.locale);
}

function surtitleTitle(languages: readonly string[]): string {
  return fillTemplate(strings.value.surtitles, {
    languages: formatLanguages(languages, props.payload.locale),
  });
}
</script>

<template>
  <div class="lr42-schedule" :class="`lr42-schedule--${params.templateVariant}`">
    <template v-if="groups.length > 0">
      <section v-for="group in groups" :key="group.dayKey" class="lr42-schedule__day">
        <h2
          v-if="params.groupBy === 'day'"
          class="lr42-schedule__day-label"
          :class="{ 'is-today': group.isToday }"
        >
          {{ dayLabel(group.dayKey) }}
        </h2>

        <ul class="lr42-schedule__rows">
          <li
            v-for="item in group.items"
            :key="item.id"
            class="lr42-schedule__row"
            :class="{
              'is-cancelled': item.status === 'cancelled',
              'is-postponed': item.status === 'postponed',
              'is-running': isInProgress(item, now),
            }"
          >
            <span class="lr42-schedule__time">{{
              formatTime(item.startsAt, payload, strings)
            }}</span>
            <span class="lr42-schedule__text">
              <span class="lr42-schedule__title">{{ item.production.title }}</span>
              <span v-if="item.production.subtitle" class="lr42-schedule__subtitle">
                {{ item.production.subtitle }}
              </span>
              <span
                v-if="surtitles(item.surtitleLanguages).length > 0"
                class="lr42-schedule__surtitles"
                :title="surtitleTitle(item.surtitleLanguages)"
              >
                <span
                  v-for="language in surtitles(item.surtitleLanguages)"
                  :key="language.code"
                  class="lr42-schedule__surtitle"
                  >{{ language.label }}</span
                >
              </span>
            </span>
            <span v-if="showLocation" class="lr42-schedule__location">{{
              item.location.name
            }}</span>
            <span class="lr42-schedule__flags">
              <span v-if="isInProgress(item, now)" class="lr42-schedule__flag">
                {{ strings.inProgress }}
              </span>
              <span
                v-if="statusLabel(item.status)"
                class="lr42-schedule__flag lr42-schedule__flag--alert"
              >
                {{ statusLabel(item.status) }}
              </span>
              <span v-else-if="item.soldOut" class="lr42-schedule__flag">{{
                strings.soldOut
              }}</span>
            </span>
          </li>
        </ul>
      </section>

      <p v-if="moreLabel" class="lr42-schedule__more">{{ moreLabel }}</p>
    </template>

    <p v-else class="lr42-schedule__empty">{{ strings.noPerformances }}</p>
  </div>
</template>

<style scoped>
.lr42-schedule {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: var(--lr42-padding, 3%);
  background: var(--lr42-bg, transparent);
  color: var(--lr42-fg, #f6eedc);
  font-family: var(--lr42-font, Georgia, 'Times New Roman', serif);
  display: flex;
  flex-direction: column;
  gap: var(--lr42-gap, 1.5%);
  overflow: hidden;
}
.lr42-schedule__day {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  min-width: 0;
}
.lr42-schedule__day-label {
  margin: 0;
  font-family: var(--lr42-meta-font, var(--lr42-font, inherit));
  font-size: var(--lr42-day-size, clamp(12px, 1.5vw, 24px));
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--lr42-muted, rgba(246, 238, 220, 0.55));
  border-bottom: 1px solid var(--lr42-rule, rgba(246, 238, 220, 0.14));
  padding-bottom: 0.3em;
}
.lr42-schedule__day-label.is-today {
  color: var(--lr42-accent, #e2b44e);
}
.lr42-schedule__rows {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--lr42-row-gap, 0.5em);
}
.lr42-schedule__row {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: baseline;
  gap: 0 1em;
  min-width: 0;
}
.lr42-schedule__time {
  font-size: var(--lr42-time-size, clamp(16px, 2vw, 34px));
  color: var(--lr42-accent, #e2b44e);
  font-variant-numeric: tabular-nums;
}
.lr42-schedule__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.lr42-schedule__title {
  font-size: var(--lr42-title-size, clamp(16px, 2.2vw, 38px));
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.is-cancelled .lr42-schedule__title,
.is-postponed .lr42-schedule__title {
  text-decoration: line-through;
  text-decoration-thickness: 0.05em;
  color: var(--lr42-muted, rgba(246, 238, 220, 0.6));
}
.lr42-schedule__subtitle,
.lr42-schedule__surtitles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35em;
}
.lr42-schedule__surtitle {
  padding: 0.05em 0.4em;
  border: 1px solid currentColor;
  border-radius: var(--lr42-radius, 2px);
  color: var(--lr42-accent, #e2b44e);
}
.lr42-schedule__surtitles,
.lr42-schedule__surtitle,
.lr42-schedule__location {
  font-size: var(--lr42-body-size, clamp(11px, 1.3vw, 22px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.6));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lr42-schedule__flags {
  display: flex;
  gap: 0.5em;
}
.lr42-schedule__flag {
  padding: 0.1em 0.5em;
  border: 1px solid currentColor;
  border-radius: var(--lr42-radius, 2px);
  font-family: var(--lr42-meta-font, var(--lr42-font, inherit));
  font-size: var(--lr42-flag-size, clamp(9px, 1vw, 16px));
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}
.lr42-schedule__flag--alert {
  color: var(--lr42-cancelled, #f5b8a0);
}
.lr42-schedule__more,
.lr42-schedule__empty {
  margin: 0;
  font-size: var(--lr42-body-size, clamp(11px, 1.3vw, 22px));
  color: var(--lr42-muted, rgba(246, 238, 220, 0.45));
  font-style: italic;
}
.lr42-schedule__empty {
  margin: auto;
}

/* Grid: cards, for a wide board with few rows. */
.lr42-schedule--grid .lr42-schedule__rows {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(28%, 1fr));
  gap: var(--lr42-row-gap, 0.8em);
}
.lr42-schedule--grid .lr42-schedule__row {
  grid-template-columns: 1fr;
  align-content: start;
  gap: 0.2em;
  padding: 0.6em 0.8em;
  border: 1px solid var(--lr42-rule, rgba(246, 238, 220, 0.14));
  border-radius: var(--lr42-radius, 2px);
}

/* Compact: portrait door panels, where vertical space is the constraint. */
.lr42-schedule--compact {
  gap: 0.6em;
}
.lr42-schedule--compact .lr42-schedule__row {
  gap: 0 0.7em;
}
.lr42-schedule--compact .lr42-schedule__subtitle,
.lr42-schedule--compact .lr42-schedule__surtitles {
  display: none;
}
.lr42-schedule--compact .lr42-schedule__title {
  font-size: var(--lr42-title-size, clamp(13px, 1.7vw, 26px));
}
</style>
